import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import "./profile.scss";

/** Derive up-to-2-char initials from a full name string */
function getInitials(name = "") {
    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

/* ── Inline SVG icons (no external dependency) ─────────────────────────── */
const IconPerson = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);
const IconMail = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);
const IconShield = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);
const IconPhone = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.56 2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.2.96.47 1.9.82 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.27-1.27a2 2 0 0 1 2.11-.45c.9.35 1.85.62 2.81.82A2 2 0 0 1 22 16.92z" />
    </svg>
);
const IconBuilding = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
);

/** Single labelled row with icon */
function DetailRow({ icon, label, value }) {
    if (!value) return null;
    return (
        <div className="profile-row">
            <span className="profile-row__label">
                <span className="profile-row__icon">{icon}</span>
                {label}
            </span>
            <span className="profile-row__value">{value}</span>
        </div>
    );
}

function ProfilePage() {
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);

    const fullName = user?.name || "";
    const initials = getInitials(fullName);
    const rawRoleName = user?.role?.name || user?.role || "";
    const roleName = rawRoleName
        ? t(`roles.types.${rawRoleName}`, { defaultValue: rawRoleName })
        : t("common.unknown");

    return (
        <div className="profile-page">
            {/* ── Hero banner ── */}
            <div className="profile-hero">
                <div className="profile-hero__inner">
                    <h1 className="profile-hero__title">{t("profile.title")}</h1>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="profile-content">
                <div className="profile-body">
                    {/* ── Avatar card ── */}
                    <div className="profile-avatar-card">
                        <div className="profile-avatar">{initials}</div>
                        <p className="profile-avatar__name">{fullName}</p>
                        <span className="profile-avatar__role">{roleName}</span>
                    </div>

                    {/* ── Details card ── */}
                    <div className="profile-details-card">
                        <p className="profile-details-card__heading">{t("profile.account-details")}</p>
                        <DetailRow icon={<IconPerson />} label={t("users.name")} value={fullName} />
                        <DetailRow icon={<IconMail />} label={t("users.email")} value={user?.email} />
                        <DetailRow icon={<IconShield />} label={t("users.role")} value={roleName} />
                        <DetailRow icon={<IconPhone />} label={t("users.phone")} value={user?.phoneNumber} />
                        <DetailRow icon={<IconBuilding />} label={t("users.company")} value={user?.company?.name} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProfilePage;
